import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { PaymentType } from '@eventmanager/types';
import { CircularProgress } from '@material-ui/core';
import { Api, isPending, isRejected } from '../../utils';
import { findOrCreateUser } from '../../actions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  currentUser,
  currentUserPermissions,
  getStatusOfGetUser,
} from '../../selectors';
import { Toolbar } from '../../components/Toolbar';
import { Clickable, ClickableVariant } from '../../components/Clickable';
import { PlanDetails } from '../../components/PlanDetails';
import { PlanHeader } from '../../components/PlanHeader';
import UserDetailsForm from '../../components/forms/UserDetailsForm';
import { Status } from '../../components/Status';

const PAGE_TITLE = 'Account';

// [2026-03-08] Refactored AccountPage to manage subscriptions.
// Users can access the Stripe Billing Portal, and free-plan users are redirected to the pricing page.
export function AccountPage() {
  const [, setLocation] = useLocation();
  const dispatch = useAppDispatch();

  const [isLoading, setLoading] = useState(false);

  const user = useAppSelector(currentUser);
  const { permissions: plan } = useAppSelector(currentUserPermissions);
  const status = useAppSelector(getStatusOfGetUser);
  const isMakingRequest = isPending(status);
  const hasError = isRejected(status);

  useEffect(() => {
    findOrCreateUser(dispatch);
  }, []);

  // Handles subscription management for the current user.
  const handleSubscriptionManagement = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Parameters used to request a Billing Portal session
      const paymentIntentParams = {
        paymentType: 'changePlan' as PaymentType, // Indicates subscription management
        userId: user.userId,
      };

      // Request the Billing Portal session URL from the API
      const response = await Api.newPaymentIntent(paymentIntentParams);
      if (!response?.url) {
        // [2026-05-18] Missing Stripe portal URL: redirect to error page instead of leaving spinner stuck.
        setLoading(false);
        setLocation('/billing-error?reason=billing_portal_failed');
        return;
      }
      setLoading(false);

      // Redirect the user to the Stripe Billing Portal
      window.location.href = response.url;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      // [2026-05-18] Same recovery path for any thrown billing portal failure.
      setLoading(false);
      setLocation('/billing-error?reason=billing_portal_failed');
    }
  };

  const handleAccountStatusAction = () => {
    if (user?.role === 'free' || user?.role === 'expired') {
      // Navigate to your internal pricing page
      setLocation('/pricing');
    } else {
      // Redirect to endpoint that creates a Stripe Portal session
      handleSubscriptionManagement();
    }
  };

  // Replace the fixed load component with a new interactive load component.
  const fallback = (
    <div className="'flex flex-1 flex-col items-center justify-center'">
      <Status
        className="color-shade-80"
        withLoader={isMakingRequest}
        message={
          isMakingRequest ? (
            `Loading...`
          ) : hasError ? (
            'Error retrieving ser!'
          ) : (
            <span>User not found</span>
          )
        }
      />
    </div>
  );

  return (
    <div className="page-inner bg-color-gradient-faded">
      <Toolbar title={PAGE_TITLE} classNames="bg-frosted" />
      <div className="flex-1 flex flex-col lg:flex-row p1 gap-1 page-inner__content">
        {user ? (
          <>
            <div className="flex lg:col-4 flex-col gap-2 bg-color-white shadow-sm br_25 p2">
              <h3 className="color-shade-80 body bold">My Plan</h3>
              {plan && (
                <div className="flex flex-col items-center gap-1">
                  {/* [2026-05-01] Render renewal section for users with an expired subscription plan */}
                  {plan.role === 'expired' ? (
                    <>
                      <div className="text-center">
                        <h3 className="color-shade-100">Plan Expired</h3>
                        <p className="color-shade-60 body-sm p1">
                          Renew your subscription to keep enjoying our features.
                        </p>
                      </div>
                      <Clickable
                        variant={ClickableVariant.BUTTON_PRIMARY}
                        onClick={handleAccountStatusAction}
                        style={{ width: 'auto' }}
                      >
                        Renew Now
                      </Clickable>
                    </>
                  ) : (
                    <>
                      {/* Replace PlanBadge with PlanHeader to include plan image */}
                      <PlanHeader planName={plan.role} />
                      <PlanDetails />
                      <Clickable
                        variant={ClickableVariant.BUTTON_SECONDARY}
                        onClick={handleAccountStatusAction}
                        style={{ width: 'auto' }} // Ensures it retains its current length
                      >
                        {plan.role === 'free' ? (
                          'Try a plan'
                        ) : isLoading ? (
                          // Add loading spinner while fetching Stripe Billing Portal URL
                          <div className="flex justify-center">
                            Redirecting....
                            <CircularProgress
                              style={{
                                color: 'white',
                                width: 20,
                                height: 20,
                                marginLeft: '10px',
                              }}
                            />
                          </div>
                        ) : (
                          'Manage Subscription'
                        )}
                      </Clickable>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex lg:col-4 xl:col-5 flex-col gap-2 bg-color-white shadow-sm br_25 p2">
              <h3 className="color-shade-80 body bold">My Profile</h3>
              <UserDetailsForm {...user} />
            </div>
          </>
        ) : (
          fallback
        )}
        {hasError && (
          <p className="App-error">Error retrieving current user!</p>
        )}
      </div>
    </div>
  );
}
